import React, { useState } from "react";
import { X, Shield, User, AlertCircle } from "lucide-react";
import useFinance from "../../../hooks/useFinance";
import PermissionBadge from "../Common/PermissionBadge";

export default function GlobalPermissions({
  isOpen,
  onClose,
  permissions,
  onUpdate,
}) {
  const { updateGlobalPermission } = useFinance();
  const [updating, setUpdating] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  if (!isOpen) return null;

  const handlePermissionChange = async (userId, newPermission) => {
    if (updating) return;

    setUpdating(true);
    try {
      const result = await updateGlobalPermission(userId, newPermission);
      if (result.ok) {
        window.showToast?.("권한이 변경되었습니다.", "success");
        onUpdate();
        setSelectedUser(null);
      } else if (result.denied) {
        window.showToast?.("권한 변경 권한이 없습니다.", "error");
      } else {
        window.showToast?.("권한 변경에 실패했습니다.", "error");
      }
    } catch (error) {
      console.error("Permission update error:", error);
      window.showToast?.("권한 변경 중 오류가 발생했습니다.", "error");
    } finally {
      setUpdating(false);
    }
  };

  const confirmPermissionChange = (user, newPermission) => {
    setSelectedUser({ ...user, newPermission });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <h2 className="text-xl font-bold">전체 권한 관리</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="mt-2 text-blue-100 text-sm">
            재무 관리 시스템의 전체 권한을 관리합니다. Creator 권한을 가진
            사용자만 테이블을 생성할 수 있습니다.
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-3">
            {permissions.map((user) => (
              <div
                key={user.user_id}
                className={`border rounded-lg p-4 transition-all ${
                  user.is_me
                    ? "border-blue-300 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {user.user_name}
                        {user.is_me && (
                          <span className="ml-2 text-sm text-blue-600">
                            (나)
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">
                        ID: {user.user_id}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <PermissionBadge type="global" level={user.access_level} />

                    {!user.is_me && (
                      <select
                        value={user.access_level}
                        onChange={(e) =>
                          confirmPermissionChange(user, e.target.value)
                        }
                        disabled={updating}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                      >
                        <option value="creator">Creator</option>
                        <option value="non_creator">Non-Creator</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {permissions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>권한 정보가 없습니다.</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 text-amber-600 mb-4">
              <AlertCircle className="w-6 h-6" />
              <h3 className="text-lg font-semibold">권한 변경 확인</h3>
            </div>

            <p className="text-gray-700 mb-6">
              <span className="font-medium">{selectedUser.user_name}</span>님의
              권한을
              <span className="font-medium text-blue-600 mx-1">
                {selectedUser.access_level === "creator"
                  ? "Creator"
                  : "Non-Creator"}
              </span>
              에서
              <span className="font-medium text-red-600 mx-1">
                {selectedUser.newPermission === "creator"
                  ? "Creator"
                  : "Non-Creator"}
              </span>
              (으)로 변경하시겠습니까?
            </p>

            {selectedUser.newPermission === "non_creator" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-amber-800">
                  Non-Creator로 변경하면 더 이상 테이블을 생성할 수 없습니다.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedUser(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => {
                  handlePermissionChange(
                    selectedUser.user_id,
                    selectedUser.newPermission
                  );
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                변경
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
